import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getRemoteConfig, fetchAndActivate, getString } from 'firebase/remote-config';

// Determine runtime environment (CRA sets NODE_ENV) and custom REACT_APP_ENV (optional)
const runtimeEnv = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

// Log early to help diagnose wrong config selection
console.log('[Firebase] Runtime environment:', runtimeEnv);

// Firebase configuration sourced from env vars injected at build time
// In CRA production builds, only .env.production is loaded. To build a dev host with dev config
// use the custom build:dev script which temporarily swaps env files.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Safety warnings if mismatch detected
if (runtimeEnv === 'development' && firebaseConfig.projectId?.includes('-65eef')) {
  console.warn('[Firebase] WARNING: Production projectId detected in development environment:', firebaseConfig.projectId);
}
if (runtimeEnv === 'production' && firebaseConfig.projectId?.includes('-dev')) {
  console.warn('[Firebase] WARNING: Development projectId detected in production build:', firebaseConfig.projectId);
}
console.log('[Firebase] Using projectId:', firebaseConfig.projectId);

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Export app for use in other modules (e.g., Firebase Functions)
export { app };

// Remote Config setup
export const remoteConfig = getRemoteConfig(app);
remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour

// Initialize and fetch remote config
export const initializeRemoteConfig = async () => {
  try {
    await fetchAndActivate(remoteConfig);
    console.log('Remote config fetched successfully');
  } catch (error) {
    console.error('Error fetching remote config:', error);
  }
};

// Get RevenueCat project ID from Remote Config
export const getRevenueCatProjectId = (): string => {
  try {
    return getString(remoteConfig, 'revcat_project_id') || process.env.REACT_APP_REVENUECAT_PROJECT_ID || '';
  } catch (error) {
    console.error('Error getting RevenueCat project ID from remote config:', error);
    return process.env.REACT_APP_REVENUECAT_PROJECT_ID || '';
  }
};

// Get onboarding feature flag from Remote Config (defaults to off)
export const getOnboardingEnabled = (): boolean => {
  try {
    const value = getString(remoteConfig, 'onboarding_enabled');
    if (value === '') {
      return process.env.REACT_APP_ONBOARDING_ENABLED === 'true';
    }
    return value === 'true';
  } catch (error) {
    console.error('Error getting onboarding_enabled from remote config:', error);
    return process.env.REACT_APP_ONBOARDING_ENABLED === 'true';
  }
};

// Get trial duration from Remote Config (with fallback to default)
export const getTrialDuration = (): string | null => {
  try {
    const trialDuration = getString(remoteConfig, 'subscription_trial_duration');
    return trialDuration || process.env.REACT_APP_TRIAL_DURATION || null; // Default to null if not set
  } catch (error) {
    console.error('Error getting trial duration from remote config:', error);
    return process.env.REACT_APP_TRIAL_DURATION || null; // Fallback to env var or null
  }
};
